import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Input, Label } from "reactstrap";
import { isEmpty } from "lodash";

import {
  getOrganisationDepartmentList,
  getOrganisationDepartmentToStaffList,
} from "../../../../../store/staff/action";
import LoadingSpinner from "../../../../../Components/Common/Spinner";
import InfoCard from "../../../../../Components/Common/InfoCard/InfoCard";
import { parentIdOf } from "../departmentHierarchy";
import OrgChart, { DEFAULT_BG, DEFAULT_TEXT } from "./OrgChart";
import "./topology.scss";

/**
 * Task 24461 - the department hierarchy as a diagram.
 *
 * The Departments tab is a flat table; the nesting only existed as a
 * Parent___OrganisationDepartmentHierarchy_Id on each row, so the shape of the
 * organisation was something you had to reconstruct in your head. This draws
 * it.
 *
 * Built entirely from the two lists the other tabs already load - no endpoint.
 */
export default function DepartmentTopology() {
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    dispatch(getOrganisationDepartmentList());
    dispatch(getOrganisationDepartmentToStaffList());
  }, [dispatch]);

  const { departments, loading, departmentToStaff } = useSelector((state) => ({
    departments:
      state.Staff.organisationDepartmentList?.organisationdepartment_list,
    loading: state.Staff.loadingOrganisationDepartmentList,
    departmentToStaff:
      state.Staff.organisationDepartmentToStaffList
        ?.organisationdepartmenttostaff_list,
  }));

  // Headcount per department, for the badge on each node.
  const staffCountByDepartment = useMemo(() => {
    const counts = new Map();
    (departmentToStaff || []).forEach((row) => {
      const key = String(row?.OrganisationDepartment_Id);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [departmentToStaff]);

  // Change #100316 - the people themselves, so the diagram reads as a staff
  // tree rather than just department names with a number. Managers first,
  // then A-Z, so the head of each department sits directly under it.
  const [showPeople, setShowPeople] = useState(true);
  const staffByDepartment = useMemo(() => {
    const byDept = new Map();
    (departmentToStaff || []).forEach((row) => {
      const key = String(row?.OrganisationDepartment_Id);
      if (!byDept.has(key)) byDept.set(key, []);
      byDept.get(key).push(row);
    });
    byDept.forEach((rows) =>
      rows.sort((a, b) => {
        const managerDiff =
          Number(b?.FlagManager === "1" || b?.FlagManager === 1) -
          Number(a?.FlagManager === "1" || a?.FlagManager === 1);
        if (managerDiff) return managerDiff;
        return (a?.Staff_FullName || "").localeCompare(
          b?.Staff_FullName || "",
          undefined,
          { sensitivity: "base", numeric: true }
        );
      })
    );
    return byDept;
  }, [departmentToStaff]);

  /**
   * Turn the flat list into a forest.
   *
   * Two things have to be survived here, because both are reachable through
   * the UI and either one would otherwise blank the screen:
   *   - an ORPHAN, whose parent has been deleted — it is treated as a root
   *     rather than vanishing, and flagged, because a department you cannot
   *     see is worse than one drawn in the wrong place;
   *   - a CYCLE (A parents B, B parents A), which nothing prevents at the
   *     data layer and which would recurse forever while rendering.
   */
  const { roots, orphanIds, unreachableIds } = useMemo(() => {
    const list = departments || [];
    const byId = new Map(list.map((d) => [String(d.Id), d]));
    const childrenOf = new Map();
    const orphans = new Set();

    list.forEach((d) => {
      // Task 24468 - shared with the Parent Department column: "0" is a
      // string here and truthy, so this used to read a top-level department
      // as having a parent that does not exist and badge it "Orphaned".
      const parentKey = parentIdOf(d);
      const isRoot = !parentKey || !byId.has(parentKey);
      if (parentKey && !byId.has(parentKey)) orphans.add(String(d.Id));

      const key = isRoot ? "__root__" : parentKey;
      if (!childrenOf.has(key)) childrenOf.set(key, []);
      childrenOf.get(key).push(d);
    });

    // Stable A-Z within each level.
    childrenOf.forEach((kids) =>
      kids.sort((a, b) =>
        (a?.Name || "").localeCompare(b?.Name || "", undefined, {
          sensitivity: "base",
          numeric: true,
        })
      )
    );

    // Every department drawn is recorded, so we can tell afterwards whether
    // any were left out entirely.
    const drawn = new Set();

    const build = (department, seen) => {
      const key = String(department.Id);
      drawn.add(key);
      // Already on this branch => cycle. Stop and mark it.
      if (seen.has(key)) return { department, children: [], cyclic: true };
      const nextSeen = new Set(seen).add(key);
      return {
        department,
        cyclic: false,
        children: (childrenOf.get(key) || []).map((child) =>
          build(child, nextSeen)
        ),
      };
    };

    const builtRoots = (childrenOf.get("__root__") || []).map((d) =>
      build(d, new Set())
    );

    // A cycle with no way in (A parents B while B parents A, or a department
    // that is its own parent) has no root, so nothing above would ever reach
    // it and those departments would silently vanish from the diagram. Pull
    // anything still undrawn up to the top level and flag it, on the same
    // principle as the orphans: better shown in the wrong place than not shown.
    const unreachable = list.filter((d) => !drawn.has(String(d.Id)));
    unreachable.forEach((d) => {
      if (!drawn.has(String(d.Id))) builtRoots.push(build(d, new Set()));
    });

    return {
      roots: builtRoots,
      orphanIds: orphans,
      unreachableIds: new Set(unreachable.map((d) => String(d.Id))),
    };
  }, [departments]);

  /**
   * Change #100316 - the department forest turned into an org chart tree.
   *
   * Each department is a card. Under it sit its managers; the first manager
   * carries the rest of the department's people and its sub-departments, so it
   * reads as "head of department -> team". With no manager flagged, the people
   * and sub-departments hang straight off the department card.
   *
   * Ids are numbered as the tree is walked, so a department repeated by a
   * parent loop (cut short and badged "Repeats") never clashes with itself.
   * The walk is deterministic, so collapse state survives a refresh of the
   * same data.
   */
  const chartRoots = useMemo(() => {
    let seq = 0;
    const isManager = (row) =>
      row?.FlagManager === "1" || row?.FlagManager === 1;

    const personNode = (row, colour, children = []) => ({
      id: `p-${row.Id}-${seq++}`,
      kind: "person",
      data: {
        staffId: row?.Staff_Id,
        name: row?.Staff_FullName || `Staff #${row?.Staff_Id}`,
        jobTitle: row?.Staff_JobTitle,
        imageUrl: row?.Staff_ImageUrl,
        isManager: isManager(row),
        deptColour: colour,
      },
      children,
    });

    const deptNode = (node) => {
      const d = node.department;
      const key = String(d.Id);
      const colour = d?.CssColourText || DEFAULT_TEXT;
      const flags = [];
      if (orphanIds.has(key))
        flags.push({
          label: "Orphaned",
          className: "bg-warning-subtle text-warning",
          title: "Its parent department no longer exists, so it is shown at the top level.",
        });
      if (unreachableIds.has(key))
        flags.push({
          label: "Loop",
          className: "bg-danger-subtle text-danger",
          title: "This department is part of a parent loop with no top level, so it is shown here to keep it visible.",
        });
      if (node.cyclic)
        flags.push({
          label: "Repeats",
          className: "bg-secondary-subtle text-secondary",
          title: "Already shown further up this branch - the chain is cut here so it does not repeat forever.",
        });

      const id = `d-${key}-${seq++}`;
      const subDepts = node.children.map(deptNode);
      const people = showPeople ? staffByDepartment.get(key) || [] : [];
      const managers = people.filter(isManager);
      const members = people.filter((r) => !isManager(r));

      let children;
      if (managers.length) {
        const [head, ...otherHeads] = managers;
        children = [
          personNode(head, colour, [
            ...members.map((r) => personNode(r, colour)),
            ...subDepts,
          ]),
          ...otherHeads.map((r) => personNode(r, colour)),
        ];
      } else {
        children = [...members.map((r) => personNode(r, colour)), ...subDepts];
      }

      return {
        id,
        kind: "dept",
        data: {
          name: d?.Name || `Department #${d.Id}`,
          description: d?.Description,
          count: staffCountByDepartment.get(key) || 0,
          bgColour: d?.CssColourBackground || DEFAULT_BG,
          textColour: colour,
          flags,
        },
        children,
      };
    };

    return roots.map(deptNode);
  }, [
    roots,
    showPeople,
    staffByDepartment,
    staffCountByDepartment,
    orphanIds,
    unreachableIds,
  ]);

  const openStaff = useCallback(
    (staffId) => {
      if (staffId) history.push(`/staff-details/${staffId}/Overview`);
    },
    [history]
  );

  if (loading) return <LoadingSpinner />;

  return (
    <InfoCard
      title="Department Topology"
      icon="ri-node-tree"
      accent="indigo"
    >
      {isEmpty(departments) ? (
        <p className="text-muted mb-0">
          No departments yet. Create one on the Departments tab and it will
          appear here.
        </p>
      ) : (
        <>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
            <p className="text-muted small mb-0">
              Every department with its people from Department To Staff -
              managers lead their team. Scroll to zoom, drag to pan, click a
              person to open their profile.
            </p>
            <div className="form-check form-switch mb-0">
              <Input
                type="switch"
                id="dept-topology-show-people"
                className="form-check-input"
                checked={showPeople}
                onChange={(e) => setShowPeople(e.target.checked)}
              />
              <Label
                className="form-check-label small"
                for="dept-topology-show-people"
              >
                Show people
              </Label>
            </div>
          </div>
          <OrgChart
            roots={chartRoots}
            onOpenStaff={openStaff}
            height="calc(100vh - 360px)"
          />
        </>
      )}
    </InfoCard>
  );
}
